import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Trophy, Medal, Award } from 'lucide-react'
import type { AttendantRanking, RankingItem } from '@/types/attendance-metrics'

interface AttendantRankingCardProps {
  rankings: AttendantRanking | null
  isLoading?: boolean
  limit?: number
}

const getMedalIcon = (position: number) => {
  switch (position) {
    case 1:
      return <Trophy className="h-5 w-5 text-yellow-500" />
    case 2:
      return <Medal className="h-5 w-5 text-gray-400" />
    case 3:
      return <Award className="h-5 w-5 text-amber-600" />
    default:
      return null
  }
}

export function AttendantRankingCard({ rankings, isLoading, limit = 10 }: AttendantRankingCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Rankings</CardTitle>
          <CardDescription>Top atendentes por métrica</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!rankings) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Rankings</CardTitle>
          <CardDescription>Top atendentes por métrica</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            Sem dados para ranking no período selecionado
          </p>
        </CardContent>
      </Card>
    )
  }

  const renderRankingList = (data: RankingItem[]) => {
    if (!data || data.length === 0) {
      return (
        <p className="text-sm text-muted-foreground text-center py-4">
          Nenhum atendente com dados para esta métrica
        </p>
      )
    }

    return (
      <>
        {data.slice(0, limit).map((attendant, index) => {
          const position = index + 1

          return (
            <div
              key={attendant.attendantOdataId}
              className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex items-center justify-center w-8 h-8 shrink-0">
                  {position <= 3 ? (
                    getMedalIcon(position)
                  ) : (
                    <span className="text-sm font-semibold text-muted-foreground">
                      {position}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{attendant.attendantName}</p>
                  <p className="text-xs text-muted-foreground">ID: {attendant.attendantOdataId}</p>
                </div>
              </div>
              <Badge
                variant={position <= 3 ? 'default' : 'outline'}
                className={position === 1 ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
              >
                {attendant.value}
              </Badge>
            </div>
          )
        })}
      </>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Rankings</CardTitle>
        <CardDescription>Top {limit} atendentes por métrica</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="responses" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="responses">Respostas</TabsTrigger>
            <TabsTrigger value="messages">Mensagens</TabsTrigger>
            <TabsTrigger value="tickets">Atendimentos</TabsTrigger>
          </TabsList>

          <TabsContent value="responses" className="space-y-3 mt-4">
            {renderRankingList(rankings.byResponses)}
          </TabsContent>

          <TabsContent value="messages" className="space-y-3 mt-4">
            {renderRankingList(rankings.byMessages)}
          </TabsContent>

          <TabsContent value="tickets" className="space-y-3 mt-4">
            {renderRankingList(rankings.byTickets)}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  )
}
